import { fontStack, isLightBackground, type ShareMode, type WaitingRoomDto } from '@praesto/shared'
import { Brandmark } from '../components/Brandmark.js'
import { useText } from '../i18n.js'

/**
 * Wartezustand der geführten Freigabe-Modi.
 *
 * Der Gast bekommt den Board-Inhalt in diesen Modi serverseitig noch gar nicht
 * ausgeliefert (siehe `realtime/ws.ts`) — dieser Bildschirm erklärt also nicht
 * bloß eine leere Fläche, er ist der ehrliche Zustand.
 *
 * Er trägt trotzdem den Auftritt der Organisation: Farben, Schriften und Logo
 * kommen aus Postgres, nicht aus dem Board. Ein Wartebildschirm, der sich am
 * Board bediente, hätte die Sperre ausgehebelt, die ihn nötig macht.
 */
export function WaitingScreen({
  boardTitle,
  mode,
  waiting,
}: {
  boardTitle: string
  mode: ShareMode
  /** Fehlt, solange der Auftritt nicht geladen werden konnte — dann bleibt es beim Hausschema. */
  waiting: WaitingRoomDto | null
}): React.ReactElement {
  const t = useText()
  const light = waiting ? isLightBackground(waiting.background) : false

  const style = waiting
    ? ({
        backgroundColor: waiting.background,
        '--waiting-accent': waiting.accent,
        // Auf hellem Untergrund wäre helle Schrift unlesbar. Die Entscheidung
        // fällt aus der Helligkeit, nicht aus einer zweiten Farbeinstellung, die
        // jemand pflegen müsste.
        '--waiting-ink': light ? '#14141a' : '#f4f2ee',
        '--waiting-muted': light ? 'rgba(20, 20, 26, 0.62)' : 'rgba(244, 242, 238, 0.66)',
        '--waiting-heading': fontStack(waiting.headingFont),
        '--waiting-body': fontStack(waiting.bodyFont),
      } as React.CSSProperties)
    : undefined

  return (
    <div className={`waiting${waiting ? ' is-branded' : ' brand-surface'}`} style={style}>
      <div className="waiting-card">
        {waiting?.logoUrl ? (
          <img className="waiting-logo" src={waiting.logoUrl} alt={waiting.orgName} />
        ) : (
          <Brandmark />
        )}

        {waiting && <p className="waiting-org">{waiting.orgName}</p>}
        <p className="waiting-title">{boardTitle}</p>

        {waiting?.greeting ? (
          <p className="waiting-greeting">{waiting.greeting}</p>
        ) : (
          <p className="muted">{t('waiting.notStarted')}</p>
        )}

        <span className="waiting-pulse" aria-hidden="true">
          <i />
          <i />
          <i />
        </span>

        {waiting?.greeting && (
          <p className="muted small">{t('waiting.changesItself')}</p>
        )}

        {mode === 'locked' && (
          <p className="muted small waiting-note">{t('waiting.lockedNote')}</p>
        )}
      </div>

      {/* Wer wartet, schaut auf diese Fläche. Dass darunter Præsto steckt, darf
          man dann auch lesen können — dezent, unter dem Auftritt des Kunden. */}
      <footer className="waiting-foot">
        <span className="wordmark small">Præsto</span>
        <span className="muted small">{t('waiting.byDigitalwart')}</span>
      </footer>
    </div>
  )
}
